package ai.wagemix.web.screens.auth

import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.auth.WMAuthFieldProblems
import ai.wagemix.logic.auth.WMAuthPresenter
import ai.wagemix.logic.auth.WMAuthPresenter.Action
import ai.wagemix.logic.auth.WMAuthPresenter.Event
import ai.wagemix.logic.auth.WMAuthPresenter.Prompt
import ai.wagemix.logic.auth.WMAuthStage
import ai.wagemix.logic.auth.WMDefaultAuthPresenter
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewModel
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewStateHolder

interface WMAuthWebViewStateHolder<out P: Prompt>: FSWebPromptingViewStateHolder<P> {
    val fieldProblemsState: State<WMAuthFieldProblems>
    val stageState: State<WMAuthStage>
    val resetEmailState: State<String?>
}

abstract class WMBaseAuthWebViewModel<P: Prompt, E: Event<P>, A: Action, PR: WMAuthPresenter<P, E, A>>(
    presenter: PR,
    protected val externalNavigation: (WMNavigationRequest) -> Unit
): FSWebPromptingViewModel<P, E, A, PR>(presenter), WMAuthWebViewStateHolder<P> {

    override val fieldProblemsState: State<WMAuthFieldProblems> get() = _fieldProblemsState
    protected val _fieldProblemsState = mutableStateOf(WMAuthFieldProblems())

    override val stageState: State<WMAuthStage> get() = _stageState
    protected val _stageState = mutableStateOf(WMAuthStage.Idle)

    override val resetEmailState: State<String?> get() = _resetEmailState
    protected val _resetEmailState = mutableStateOf<String?>(null)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            fieldProblemsUpdate?.let { _fieldProblemsState.value = it }
            stageUpdate?.let { _stageState.value = it }
            resetEmailUpdate?.let { _resetEmailState.value = it }
            navigationUpdate?.let { externalNavigation(it) }
        }
    }

    fun userEditedFields(firstName: String? = null, lastName: String? = null, email: String? = null, password: String? = null, confirmPassword: String? = null) =
        presenter.userEditedFields(firstName, lastName, email, password, confirmPassword)
    fun userRequestedSignIn(email: String, password: String) = presenter.userRequestedSignIn(email, password)
    fun userRequestedRegister(firstName: String, lastName: String, email: String, password: String, confirmPassword: String) =
        presenter.userRequestedRegister(firstName, lastName, email, password, confirmPassword)
    fun userRequestedPasswordReset(email: String) = presenter.userRequestedPasswordReset(email)
    fun userArrivedWithResetCode(code: String) = presenter.userArrivedWithResetCode(code)
    fun userRequestedConfirmPasswordReset(code: String, password: String, confirmPassword: String) = presenter.userRequestedConfirmPasswordReset(code, password, confirmPassword)
    fun userArrivedWithVerificationCode(code: String) = presenter.userArrivedWithVerificationCode(code)
    fun userRequestedResendVerification() = presenter.userRequestedResendVerification()
}

typealias WMDefaultAuthWebViewModel = WMBaseAuthWebViewModel<Prompt.Base, Event.Base<Prompt.Base>, Action.Base, WMDefaultAuthPresenter>

fun createDefaultAuthWebViewModel(
    presenter: WMDefaultAuthPresenter,
    externalNavigation: (WMNavigationRequest) -> Unit
): WMDefaultAuthWebViewModel = object: WMDefaultAuthWebViewModel(presenter, externalNavigation) {
    override val defaultPrompt: Prompt.Base
        get() = Prompt.baseFactory().none()
}
